import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useStore } from 'react-redux';

import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  openReplay,
  replayContentLoaded,
  restoreState,
  selectTurn,
  updateContext,
} from '@/modules/tryIt/redux/actions';
import { selectTryItState } from '@/modules/tryIt/redux/selectors';
import { nodeToLegacyTurn } from '@/modules/tryIt/utils/utils';
import { RootState } from '@/shared/types/state';

import { LeftPanel } from './LeftPanel';
import { RightPanel } from './RightPanel';
import {
  buildTryItSessionFromTurns,
  resolveTurnRequestId,
} from '../../../buildTryItSessionFromTurns';
import { useSimulationSession } from '../../../hooks/useSimulationSession';
import type { EvaluationWithSimulation } from '../../../types';

interface SimulationPreviewDialogProps {
  evaluation: EvaluationWithSimulation;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SimulationPreviewDialog = ({
  evaluation,
  open,
  onOpenChange,
}: SimulationPreviewDialogProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const { session, isLoading, error } = useSimulationSession(
    open ? evaluation.session_id : null,
    evaluation.status
  );
  const isInitialLoading = isLoading && !session;

  const [selectedTurnIndex, setSelectedTurnIndex] = useState<number>(0);
  const hasSeededSelectionRef = useRef(false);

  const { sessionNodes, context: tryItContext } = useMemo(
    () => buildTryItSessionFromTurns(session?.turns),
    [session?.turns]
  );

  const handleSelectTurn = useCallback(
    (index: number) => {
      setSelectedTurnIndex(index);
      dispatch(selectTurn(index));
    },
    [dispatch]
  );

  const simulationContext = useMemo(
    () => ({
      brain_id: evaluation.brain_id,
      run_id: evaluation.run_id,
      evaluation_id: evaluation.evaluation_id,
    }),
    [evaluation.brain_id, evaluation.run_id, evaluation.evaluation_id]
  );

  // Preview is read-only: reuse TextResponse's `isReplay` gate so option /
  // postback buttons render disabled instead of silently swallowing clicks.
  // Restore on unmount: the slice is shared with the Try It test conversation.
  useEffect(() => {
    const previousTryItState = selectTryItState(store.getState());
    dispatch(openReplay({ sessionId: evaluation.session_id ?? '' }));
    return () => {
      dispatch(restoreState(previousTryItState));
    };
  }, [dispatch, store, evaluation.session_id]);

  // Sync simulation turns into the shared try-it Redux slice so the Details
  // and Context tabs (driven by try-it selectors) see the right data.
  useEffect(() => {
    if (!sessionNodes.length) {
      return;
    }
    dispatch(replayContentLoaded({ turns: sessionNodes.map(nodeToLegacyTurn) }));
    dispatch(updateContext(tryItContext));
    // Seed the selected turn exactly once; later selections come from user
    // clicks, not from this effect re-running on data changes.
    if (!hasSeededSelectionRef.current) {
      hasSeededSelectionRef.current = true;
      dispatch(selectTurn(0));
    }
  }, [dispatch, sessionNodes, tryItContext]);

  const detailsContent = useMemo(
    () => ({
      session_id: evaluation.session_id ?? undefined,
      brain_parent_id: session?.brain_parent_id ?? undefined,
    }),
    [evaluation.session_id, session?.brain_parent_id]
  );

  const hasTurnSelected = sessionNodes.length > 0;
  const selectedTurn = session?.turns?.[selectedTurnIndex];
  const selectedRequestId = selectedTurn ? resolveTurnRequestId(selectedTurn) : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'flex h-[80vh] max-w-7xl min-w-4xl flex-row gap-4 overflow-hidden p-1 sm:max-w-7xl',
          'rounded-xl bg-bg-muted-light',
          '[&>button]:hidden'
        )}
        // No description; opt out of the missing-description warning.
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">{t('simulations.preview')}</DialogTitle>
        <LeftPanel
          evaluation={evaluation}
          session={session}
          sessionNodes={sessionNodes}
          isInitialLoading={isInitialLoading}
          hasError={!!error}
          selectedTurnIndex={selectedTurnIndex}
          simulationContext={simulationContext}
          onSelectTurn={handleSelectTurn}
        />
        <RightPanel
          evaluation={evaluation}
          hasTurnSelected={hasTurnSelected}
          detailsContent={detailsContent}
          simulationContext={simulationContext}
          selectedRequestId={selectedRequestId}
        />
      </DialogContent>
    </Dialog>
  );
};
